import { Capacitor } from '@capacitor/core'
import { LocalStorageKeys } from '@shared/constants/LocalStorageKeys'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'

export type StoredAuthTokens = {
	accessToken: string | null
	refreshToken: string | null
	storedAt: number
}

const NATIVE_STORAGE_TIMEOUT_MS = 3_000
const STORED_AT_KEY = `${LocalStorageKeys.accessToken}:storedAt`
const TOKEN_GENERATION_KEY = `${LocalStorageKeys.accessToken}:generation`
const ACTIVE_GENERATION_KEY = `${LocalStorageKeys.accessToken}:activeGeneration`

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined

	const timeout = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(() => {
			reject(new Error('Secure storage operation timed out'))
		}, timeoutMs)
	})

	try {
		return await Promise.race([promise, timeout])
	} finally {
		if (timeoutId !== undefined) {
			clearTimeout(timeoutId)
		}
	}
}

const safeAtob = (value: string): string => {
	if (typeof window === 'undefined') return ''
	if (typeof window.atob === 'function') return window.atob(value)
	// eslint-disable-next-line no-restricted-globals
	return atob(value)
}

const base64UrlDecode = (value: string): string => {
	const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
	const padded = base64 + '==='.slice((base64.length + 3) % 4)
	return safeAtob(padded)
}

const decodeJwtPayload = (token: string): unknown => {
	const parts = token.split('.')
	if (parts.length < 2) return null
	try {
		return JSON.parse(base64UrlDecode(parts[1]))
	} catch {
		return null
	}
}

export class AuthTokenStorage {
	private memoryStorage = new Map<string, string>()
	private memoryActiveGeneration = '0'

	private isNativePlatform(): boolean {
		return Capacitor.isNativePlatform()
	}

	private cleanupLegacyWebStorage(key: string): void {
		if (this.isNativePlatform() || typeof window === 'undefined') return
		window.localStorage.removeItem(key)
	}

	private getActiveGenerationState(): { generation: string; exists: boolean } {
		if (typeof window === 'undefined') {
			return { generation: this.memoryActiveGeneration, exists: true }
		}

		const generation = window.localStorage.getItem(ACTIVE_GENERATION_KEY)
		return { generation: generation ?? '0', exists: generation !== null }
	}

	private getActiveGeneration(): string {
		return this.getActiveGenerationState().generation
	}

	public getCurrentGeneration(): string {
		return this.getActiveGeneration()
	}

	private setActiveGeneration(generation: string): void {
		this.memoryActiveGeneration = generation
		if (typeof window !== 'undefined') {
			window.localStorage.setItem(ACTIVE_GENERATION_KEY, generation)
		}
	}

	private bumpActiveGeneration(): void {
		const currentGeneration = Number(this.getActiveGeneration())
		const nextGeneration = Number.isFinite(currentGeneration) ? currentGeneration + 1 : Date.now()
		this.setActiveGeneration(String(nextGeneration))
	}

	private async getItem(key: string): Promise<string | null> {
		if (!this.isNativePlatform()) {
			this.cleanupLegacyWebStorage(key)
			return this.memoryStorage.get(key) ?? null
		}

		try {
			const result = await withTimeout(
				SecureStoragePlugin.get({ key }),
				NATIVE_STORAGE_TIMEOUT_MS
			)
			return typeof result?.value === 'string' ? result.value : null
		} catch {
			return null
		}
	}

	private async setItem(key: string, value: string): Promise<void> {
		if (!this.isNativePlatform()) {
			this.cleanupLegacyWebStorage(key)
			this.memoryStorage.set(key, value)
			return
		}

		await withTimeout(SecureStoragePlugin.set({ key, value }), NATIVE_STORAGE_TIMEOUT_MS)
	}

	private async removeItem(key: string): Promise<void> {
		if (!this.isNativePlatform()) {
			this.memoryStorage.delete(key)
			this.cleanupLegacyWebStorage(key)
			return
		}

		try {
			await withTimeout(SecureStoragePlugin.remove({ key }), NATIVE_STORAGE_TIMEOUT_MS)
		} catch {
			// ignore (missing key throws)
		}
	}

	public async getTokens(): Promise<StoredAuthTokens | null> {
		const rawAccess = await this.getItem(LocalStorageKeys.accessToken)
		const rawRefresh = await this.getItem(LocalStorageKeys.refreshToken)
		if (!rawAccess && !rawRefresh) return null

		const tokenGeneration = await this.getItem(TOKEN_GENERATION_KEY)
		const activeGenerationState = this.getActiveGenerationState()

		if (tokenGeneration && tokenGeneration !== activeGenerationState.generation) {
			await this.clearTokens()
			return null
		}

		if (!tokenGeneration && activeGenerationState.exists) {
			await this.clearTokens()
			return null
		}

		if (!tokenGeneration) {
			this.setActiveGeneration(activeGenerationState.generation)
			void this.setItem(TOKEN_GENERATION_KEY, activeGenerationState.generation).catch(() => {
				// Legacy tokens remain usable; the next successful setTokens call will persist metadata.
			})
		}

		const storedAtStr = await this.getItem(STORED_AT_KEY)
		const storedAt = storedAtStr ? Number(storedAtStr) : Date.now()
		return {
			accessToken: rawAccess,
			refreshToken: rawRefresh,
			storedAt: Number.isFinite(storedAt) ? storedAt : Date.now(),
		}
	}

	public async setTokens(
		accessToken: string,
		refreshToken: string | null,
		generation = this.getActiveGeneration()
	): Promise<void> {
		await this.setItem(TOKEN_GENERATION_KEY, generation)
		await this.setItem(LocalStorageKeys.accessToken, accessToken)
		if (refreshToken) {
			await this.setItem(LocalStorageKeys.refreshToken, refreshToken)
		} else {
			await this.removeItem(LocalStorageKeys.refreshToken)
		}
		await this.setItem(STORED_AT_KEY, String(Date.now()))
	}

	public async clearTokensForGeneration(generation: string): Promise<void> {
		const tokenGeneration = await this.getItem(TOKEN_GENERATION_KEY)
		if (tokenGeneration !== generation) return

		await this.removeItem(LocalStorageKeys.accessToken)
		await this.removeItem(LocalStorageKeys.refreshToken)
		await this.removeItem(STORED_AT_KEY)
		await this.removeItem(TOKEN_GENERATION_KEY)
	}

	public async clearTokens(): Promise<void> {
		this.bumpActiveGeneration()
		await this.removeItem(LocalStorageKeys.accessToken)
		await this.removeItem(LocalStorageKeys.refreshToken)
		await this.removeItem(STORED_AT_KEY)
		await this.removeItem(TOKEN_GENERATION_KEY)
	}

	public isTokenExpired(token: string, leewaySeconds: number = 30): boolean {
		const payload = decodeJwtPayload(token)
		if (!payload || typeof payload !== 'object') return true
		const exp = (payload as { exp?: unknown }).exp
		if (typeof exp !== 'number') return true
		const nowSeconds = Date.now() / 1000
		return nowSeconds + leewaySeconds >= exp
	}

	public isAccessTokenExpired(accessToken: string, leewaySeconds: number = 30): boolean {
		return this.isTokenExpired(accessToken, leewaySeconds)
	}

	public isRefreshTokenExpired(refreshToken: string, leewaySeconds: number = 30): boolean {
		return this.isTokenExpired(refreshToken, leewaySeconds)
	}
}

export const authTokenStorage = new AuthTokenStorage()
